// Stats at ≤880px (mobile overhaul Phase D §11) — the two pieces of maths the
// mobile page needs that the desktop render has no use for. Kept here, pure
// and React-free, so they can be unit tested (the suite is Node-only).
//
// 1. The goal card's hint line. Desktop spells it "62% of target · on pace"
//    across two spans; the mockup's card says "62% · on pace" in one, tinted
//    by how the goal is tracking (§11.1 A, Henry's decision 10 — the pace
//    hint stays, it is the point of the card).
// 2. Month-bar heights. Desktop draws a 760px viewBox with the two series
//    side by side; at 393px the mockup stacks them into one column per month
//    (revenue gold over the grey expense plinth) with percentage heights, so
//    the scale has to be the tallest STACK rather than the tallest single
//    value or the tallest column would overflow its 120px box.

/**
 * "$18.6k" — the compact money the mockup's goal cards, wrapped stats and
 * money bars are drawn with (§11.1 A/C). A 176px card cannot hold "$12,450"
 * beside "of $40,000" at 26px display type, and at a glance the rounded
 * figure is the one that's wanted; the exact numbers stay in the vitals.
 *
 * Deliberately the same rule as Overview's private `compactMoney`
 * (pages/Overview.tsx:527) so the two mobile screens round money the same
 * way — Overview's copy should fold into this one when that file is next
 * opened (it is another phase's file, so it is left alone here).
 */
export function compactMoney(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const dollars = cents / 100;
  if (Math.abs(dollars) < 1000) return `$${Math.round(dollars)}`;
  const k = Math.round((dollars / 1000) * 10) / 10;
  return `$${Number.isInteger(k) ? k.toFixed(0) : k.toFixed(1)}k`;
}

/** How the hint reads — `ok` green, `dim` for behind pace, `faint` for a
    card with no target yet. */
export type GoalHintTone = 'ok' | 'dim' | 'faint';

export interface GoalHint {
  text: string;
  tone: GoalHintTone;
}

/**
 * The mobile goal card's hint line. `pace` is `paceOf(...)`'s result — null
 * means "no pace opinion" (an unparseable period key), which reads as the
 * bare percentage rather than as "behind".
 */
export function goalHintOf(
  actual: number,
  target: number | null,
  pace: { onPace: boolean } | null,
): GoalHint {
  if (target === null || target <= 0) return { text: 'No target — tap to add', tone: 'faint' };
  const pct = Math.round((actual / target) * 100);
  if (actual >= target) return { text: `${pct}% · target ${actual > target ? 'passed' : 'hit'}`, tone: 'ok' };
  if (pace === null) return { text: `${pct}% of target`, tone: 'dim' };
  return {
    text: `${pct}% · ${pace.onPace ? 'on pace' : 'behind pace'}`,
    tone: pace.onPace ? 'ok' : 'dim',
  };
}

/** The tallest stack fills this much of the 120px chart box; the 2% left over
    is headroom so a floored sliver (below) can never push a column past it. */
export const MAX_STACK_PCT = 98;
/** A non-zero value always shows — ~1.5px of the box, the same floor the
    desktop SVG applies in pixels. */
export const MIN_VISIBLE_PCT = 1.25;

export interface MonthBarValues {
  primary: number;
  secondary: number;
}

/** Percentage heights (of the chart box) for one month's two segments. */
export interface MonthBarHeights {
  primary: number;
  secondary: number;
}

function segment(value: number, max: number): number {
  if (value <= 0) return 0;
  return Math.max(MIN_VISIBLE_PCT, Math.round((value / max) * MAX_STACK_PCT * 100) / 100);
}

/**
 * Height percentages for the mobile month bars, scaled so the tallest
 * revenue + expenses stack reaches `MAX_STACK_PCT` and no stack can ever
 * exceed 100% of the box. All-zero data gives all-zero heights (an empty
 * chart, not a full one).
 */
export function scaleMonthBars(bars: readonly MonthBarValues[]): MonthBarHeights[] {
  const max = Math.max(0, ...bars.map((b) => Math.max(0, b.primary) + Math.max(0, b.secondary)));
  if (max <= 0) return bars.map(() => ({ primary: 0, secondary: 0 }));
  return bars.map((b) => ({ primary: segment(b.primary, max), secondary: segment(b.secondary, max) }));
}
